import type { 
  SeguroDesempregoResultsType,
  SalariosDetalhamento,
  FaixaAplicada,
  NumeroParcelas,
  CalculoBreakdown,
  CronogramaParcela
} from '../calculadora-seguro-desemprego';
import { 
  CONSTANTES_SEGURO_DESEMPREGO_2025, 
  REQUISITOS_TEMPO_TRABALHO 
} from '../calculadora-seguro-desemprego';

const parseNumericValue = (value: string): number => {
  if (!value || typeof value !== 'string') return 0;
  return Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0;
};

const arredondarMoeda = (valor: number): number => Math.round(valor * 100) / 100;

/**
 * Calcula a média dos três últimos salários
 */
export const calcularMediaSalarial = (
  antepenultimo: number, 
  penultimo: number, 
  ultimo: number
): SalariosDetalhamento => {
  const media = (antepenultimo + penultimo + ultimo) / 3;
  
  return {
    antepenultimo: arredondarMoeda(antepenultimo),
    penultimo: arredondarMoeda(penultimo),
    ultimo: arredondarMoeda(ultimo),
    media: arredondarMoeda(media)
  };
};

/**
 * Calcula o valor da parcela baseado nas faixas salariais 2025
 */
export const calcularValorParcela = (salarioMedio: number): FaixaAplicada => {
  const { FAIXA_1_LIMITE, FAIXA_1_ALIQUOTA, FAIXA_2_LIMITE, FAIXA_2_BASE, FAIXA_2_ALIQUOTA, TETO_BENEFICIO, PISO_BENEFICIO } = CONSTANTES_SEGURO_DESEMPREGO_2025;
  
  let valorBruto: number;
  let faixa: 1 | 2 | 3;
  let descricao: string;
  let calculo: string;
  let limiteFaixa: string | undefined;
  
  if (salarioMedio <= FAIXA_1_LIMITE) {
    // Faixa 1: até R$ 2.138,76 - 80% do salário médio
    faixa = 1;
    valorBruto = salarioMedio * FAIXA_1_ALIQUOTA;
    descricao = `Faixa 1: até R$ ${FAIXA_1_LIMITE.toFixed(2).replace('.', ',')}`;
    calculo = `R$ ${salarioMedio.toFixed(2).replace('.', ',')} × 80% = R$ ${valorBruto.toFixed(2).replace('.', ',')}`;
    limiteFaixa = `Salário médio de até R$ ${FAIXA_1_LIMITE.toFixed(2).replace('.', ',')}`;
  } else if (salarioMedio <= FAIXA_2_LIMITE) {
    // Faixa 2: R$ 2.138,77 a R$ 3.564,96 - cálculo misto
    faixa = 2;
    const valorExcedente = salarioMedio - FAIXA_1_LIMITE;
    valorBruto = (valorExcedente * FAIXA_2_ALIQUOTA) + FAIXA_2_BASE;
    descricao = `Faixa 2: R$ ${(FAIXA_1_LIMITE + 0.01).toFixed(2).replace('.', ',')} a R$ ${FAIXA_2_LIMITE.toFixed(2).replace('.', ',')}`;
    calculo = `[(R$ ${salarioMedio.toFixed(2).replace('.', ',')} - R$ ${FAIXA_1_LIMITE.toFixed(2).replace('.', ',')}) × 50%] + R$ ${FAIXA_2_BASE.toFixed(2).replace('.', ',')} = R$ ${valorBruto.toFixed(2).replace('.', ',')}`;
    limiteFaixa = `Salário médio de R$ ${(FAIXA_1_LIMITE + 0.01).toFixed(2).replace('.', ',')} a R$ ${FAIXA_2_LIMITE.toFixed(2).replace('.', ',')}`;
  } else {
    // Faixa 3: acima de R$ 3.564,96 - valor fixo
    faixa = 3;
    valorBruto = TETO_BENEFICIO;
    descricao = `Faixa 3: acima de R$ ${FAIXA_2_LIMITE.toFixed(2).replace('.', ',')}`;
    calculo = `Valor fixo (teto): R$ ${TETO_BENEFICIO.toFixed(2).replace('.', ',')}`;
    limiteFaixa = `Salário médio acima de R$ ${FAIXA_2_LIMITE.toFixed(2).replace('.', ',')}`;
  }
  
  // Aplicar piso mínimo
  const valorFinal = Math.max(valorBruto, PISO_BENEFICIO);
  
  return {
    numero: faixa,
    descricao,
    calculo,
    valorBruto: arredondarMoeda(valorBruto),
    valorFinal: arredondarMoeda(valorFinal),
    limiteFaixa
  };
};

/**
 * Calcula o número de parcelas baseado no histórico e tempo trabalhado
 */
export const calcularNumeroParcelas = (
  historicoRecebimento: 'nunca' | 'uma_vez' | 'duas_ou_mais',
  mesesTrabalhados: number
): NumeroParcelas => {
  const requisito = REQUISITOS_TEMPO_TRABALHO[historicoRecebimento];
  
  let parcelasCalculadas: number;
  
  if (mesesTrabalhados >= 24) {
    parcelasCalculadas = 5;
  } else if (mesesTrabalhados >= 12) {
    parcelasCalculadas = 4;
  } else if (mesesTrabalhados >= 9 && historicoRecebimento !== 'nunca') {
    parcelasCalculadas = 3;
  } else if (mesesTrabalhados >= 6 && historicoRecebimento === 'duas_ou_mais') {
    parcelasCalculadas = 3;
  } else {
    parcelasCalculadas = 0; // Não tem direito
  }
  
  return {
    baseHistorico: requisito.descricao,
    mesesTrabalhados,
    mesesNecessarios: requisito.mesesMinimos,
    parcelasCalculadas,
    parcelasFinal: parcelasCalculadas
  };
};

/**
 * Gera cronograma de parcelas com datas estimadas
 */
export const gerarCronogramaParcelas = (numeroParcelas: number, valorParcela: number): Array<CronogramaParcela> => {
  const cronograma: Array<CronogramaParcela> = [];
  const dataBase = new Date();
  
  for (let i = 1; i <= numeroParcelas; i++) {
    const dataEstimada = new Date(dataBase);
    dataEstimada.setMonth(dataBase.getMonth() + i);
    
    cronograma.push({
      numero: i,
      valor: arredondarMoeda(valorParcela),
      dataEstimada: dataEstimada.toLocaleDateString('pt-BR', { 
        month: 'long', 
        year: 'numeric' 
      })
    });
  }
  
  return cronograma;
};

/**
 * Valida se o trabalhador tem direito ao seguro-desemprego
 */
export const validarElegibilidade = (
  historicoRecebimento: 'nunca' | 'uma_vez' | 'duas_ou_mais',
  mesesTrabalhados: number
): { elegivel: boolean; motivo?: string } => {
  const requisito = REQUISITOS_TEMPO_TRABALHO[historicoRecebimento];
  
  if (mesesTrabalhados < requisito.mesesMinimos) {
    const sugestao = mesesTrabalhados < 24 ? 
      ` DICA: Se conseguir trabalhar até 24+ meses, terá direito a 5 parcelas (máximo).` : '';
    
    return {
      elegivel: false,
      motivo: `Para ${requisito.descricao.toLowerCase()}, você precisa de pelo menos ${requisito.mesesMinimos} meses trabalhados. Você informou ${mesesTrabalhados} meses, faltam ${requisito.mesesMinimos - mesesTrabalhados} meses.${sugestao}`
    };
  }
  
  return { elegivel: true };
};

/**
 * Validação de parâmetros de entrada
 */
export const validarParametrosSeguroDesemprego = (
  antepenultimo: number,
  penultimo: number,
  ultimo: number,
  mesesTrabalhados: number
): void => {
  if (antepenultimo <= 0 || penultimo <= 0 || ultimo <= 0) {
    throw new Error("Todos os salários devem ser maiores que zero");
  }
  
  if (mesesTrabalhados <= 0) {
    throw new Error("Número de meses trabalhados deve ser maior que zero");
  }
  
  if (mesesTrabalhados > 99) {
    throw new Error("Número de meses trabalhados não pode ser superior a 99");
  }
};

/**
 * Função principal do cálculo do seguro-desemprego
 */
export const calcularSeguroDesemprego = (
  antepenultimoSalario: string,
  penultimoSalario: string,
  ultimoSalario: string,
  historicoRecebimento: 'nunca' | 'uma_vez' | 'duas_ou_mais',
  mesesTrabalhados: string
): SeguroDesempregoResultsType => {

  const antepenultimoNum = parseNumericValue(antepenultimoSalario);
  const penultimoNum = parseNumericValue(penultimoSalario);
  const ultimoNum = parseNumericValue(ultimoSalario);
  const mesesTrabalhadosNum = parseInt(mesesTrabalhados) || 0;

  validarParametrosSeguroDesemprego(antepenultimoNum, penultimoNum, ultimoNum, mesesTrabalhadosNum);

  // Validar elegibilidade
  const elegibilidade = validarElegibilidade(historicoRecebimento, mesesTrabalhadosNum);
  
  if (!elegibilidade.elegivel) {
    return {
      elegivel: false,
      motivoInelegibilidade: elegibilidade.motivo,
      salarioMedio: 0,
      valorParcela: 0,
      numeroParcelas: 0,
      valorTotal: 0,
      faixaAplicada: 1,
      detalhamentoCalculo: {
        salarios: calcularMediaSalarial(antepenultimoNum, penultimoNum, ultimoNum),
        faixaAplicada: {
          numero: 1,
          descricao: '',
          calculo: '',
          valorBruto: 0,
          valorFinal: 0
        },
        numeroParcelas: calcularNumeroParcelas(historicoRecebimento, mesesTrabalhadosNum)
      },
      cronogramaParcelas: []
    };
  }

  // Cálculos principais
  const salarios = calcularMediaSalarial(antepenultimoNum, penultimoNum, ultimoNum);
  const faixaAplicada = calcularValorParcela(salarios.media);
  const numeroParcelas = calcularNumeroParcelas(historicoRecebimento, mesesTrabalhadosNum);
  const valorTotal = faixaAplicada.valorFinal * numeroParcelas.parcelasFinal;
  const cronogramaParcelas = gerarCronogramaParcelas(numeroParcelas.parcelasFinal, faixaAplicada.valorFinal);

  const detalhamentoCalculo: CalculoBreakdown = {
    salarios,
    faixaAplicada,
    numeroParcelas
  };

  return {
    elegivel: true,
    salarioMedio: salarios.media,
    valorParcela: faixaAplicada.valorFinal,
    numeroParcelas: numeroParcelas.parcelasFinal,
    valorTotal: arredondarMoeda(valorTotal),
    faixaAplicada: faixaAplicada.numero,
    detalhamentoCalculo,
    cronogramaParcelas
  };
};